import { publicApi, sellerApi } from '@core/api/httpClient'
import { OWNER_ONBOARDING_ENDPOINT_PATHS } from './ownerOnboardingEndpointPaths'
import {
  parseOwnerBankAccount,
  parseOwnerKycSession,
  parseOwnerKycStatus,
  parseOwnerOnboardingContext,
  parseOwnerOnboardingCountries,
  parseOwnerOnboardingSession,
  parseSellerOwnerOnboardingAvailability,
  parseSellerOwnerOnboardingSession,
} from './ownerOnboardingParser'
import type {
  ConfirmOwnershipPayloadDto,
  OwnerApiEnvelope,
  OwnerBankAccountDto,
  OwnerKycSessionDto,
  OwnerKycStatusDto,
  OwnerOnboardingContextDto,
  OwnerOnboardingCountryDto,
  OwnerOnboardingSessionDto,
  OwnerUaePassAuthorizeUrlDto,
  CompleteOwnerUaePassVerificationPayloadDto,
  VerifyOwnerEmailOtpPayloadDto,
  ReplaceRepresentativesPayloadDto,
  RepresentativePayloadDto,
  SubmitBankAccountPayloadDto,
  SubmitCompanyProfilePayloadDto,
  SubmitPersonalDetailsPayloadDto,
  SellerOwnerOnboardingAvailabilityDto,
  SellerOwnerOnboardingSessionDto,
} from './ownerOnboardingContracts'
import type {
  ConfirmOwnershipInput,
  OwnerBankAccount,
  OwnerKycSession,
  OwnerKycState,
  OwnerOnboardingContext,
  OwnerOnboardingCountry,
  OwnerOnboardingSession,
  OwnerRepresentativeInput,
  SubmitBankAccountInput,
  SubmitCompanyProfileInput,
  SubmitPersonalDetailsInput,
  SellerOwnerOnboardingAvailability,
  SellerOwnerOnboardingSessionGrant,
} from '../types/ownerOnboardingTypes'

/**
 * Every call rides `publicApi` — the anonymous instance. The owner has no
 * auth-service identity in this flow, so the shared 401 → refresh → `/login`
 * handling on `sellerApi` would hijack them into a sign-in screen they can never
 * pass. Authorisation here is the onboarding session, attached per call.
 *
 * The session token is passed in explicitly rather than read from a store:
 * `api/` stays a pure function of its arguments, and a call that forgets it
 * fails to compile instead of silently going out unauthenticated.
 */
function sessionHeaders(sessionToken: string) {
  return { headers: { Authorization: `Bearer ${sessionToken}` } }
}

/**
 * Drops blank optional fields instead of sending `''`.
 *
 * The server marks these `@IsOptional()`, which means *absent* — an empty string
 * still reaches `@IsDateString` / `@IsUUID` and fails validation. Left in, an
 * untouched "Address line 2" would reject the whole submission.
 */
function omitBlank<T extends object>(payload: T): Partial<T> {
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value === 'string' && value.trim() === '') continue
    if (value === undefined || value === null) continue
    cleaned[key] = value
  }
  return cleaned as Partial<T>
}

// ─── Landing: invite token → session ─────────────────────────────────────────

/**
 * The one anonymous call. Exchanges the emailed invite token for a short-lived
 * session and the prefill context, and is the ONLY place the invite token is
 * ever sent. Everything after it uses the session token (plan §3.1).
 */
export async function resolveOwnerInvite(token: string): Promise<OwnerOnboardingSession> {
  const { data } = await publicApi.get<OwnerApiEnvelope<OwnerOnboardingSessionDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.RESOLVE_INVITE(token),
  )
  return parseOwnerOnboardingSession(data.data)
}

// ─── Seller-portal entry: seller JWT → session ──────────────────────────────

/**
 * The two calls that ride `sellerApi`: the caller is a signed-in seller, so the
 * shared 401 → refresh handling is exactly right here. Everything they unlock
 * still goes out on `publicApi` with the owner session, like the emailed flow.
 */
export async function getSellerOwnerOnboardingAvailability(
  listingRequestId: string,
): Promise<SellerOwnerOnboardingAvailability> {
  const { data } = await sellerApi.get<OwnerApiEnvelope<SellerOwnerOnboardingAvailabilityDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.SELLER_AVAILABILITY(listingRequestId),
  )
  return parseSellerOwnerOnboardingAvailability(data.data)
}

export async function startSellerOwnerOnboardingSession(
  listingRequestId: string,
): Promise<SellerOwnerOnboardingSessionGrant> {
  const { data } = await sellerApi.post<OwnerApiEnvelope<SellerOwnerOnboardingSessionDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.SELLER_SESSION(listingRequestId),
  )
  return parseSellerOwnerOnboardingSession(data.data)
}

export async function getOwnerOnboardingContext(
  sessionToken: string,
): Promise<OwnerOnboardingContext> {
  const { data } = await publicApi.get<OwnerApiEnvelope<OwnerOnboardingContextDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.CONTEXT,
    sessionHeaders(sessionToken),
  )
  return parseOwnerOnboardingContext(data.data)
}

/**
 * Countries for the wizard's pickers.
 *
 * Served by the owner surface, not by the shared `/api/countries`. That endpoint
 * requires a seller JWT, and the 401 it returns to an anonymous owner trips
 * `sellerApi`'s auth interceptor — which clears storage and hard-redirects to
 * `/login`, a page the owner can never pass. Keeping every call on `publicApi`
 * is what makes this flow immune to that.
 */
export async function getOwnerOnboardingCountries(
  sessionToken: string,
): Promise<OwnerOnboardingCountry[]> {
  const { data } = await publicApi.get<OwnerApiEnvelope<OwnerOnboardingCountryDto[]>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.COUNTRIES,
    sessionHeaders(sessionToken),
  )
  return parseOwnerOnboardingCountries(data.data)
}

// ─── Step 1 — identity ───────────────────────────────────────────────────────

export async function startOwnerKycSession(sessionToken: string): Promise<OwnerKycSession> {
  const { data } = await publicApi.post<OwnerApiEnvelope<OwnerKycSessionDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.KYC_SESSION,
    {},
    sessionHeaders(sessionToken),
  )
  return parseOwnerKycSession(data.data)
}

export async function initiateOwnerUaePass(sessionToken: string): Promise<string> {
  const { data } = await publicApi.post<OwnerApiEnvelope<OwnerUaePassAuthorizeUrlDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.UAE_PASS_INITIATE,
    {},
    sessionHeaders(sessionToken),
  )
  return data.data.authorizeUrl
}

export async function completeOwnerUaePass(
  sessionToken: string,
  payload: CompleteOwnerUaePassVerificationPayloadDto,
): Promise<void> {
  await publicApi.post<void>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.UAE_PASS_COMPLETE,
    payload,
    sessionHeaders(sessionToken),
  )
}

export async function sendOwnerEmailOtp(sessionToken: string): Promise<void> {
  await publicApi.post(
    OWNER_ONBOARDING_ENDPOINT_PATHS.EMAIL_OTP_SEND,
    {},
    sessionHeaders(sessionToken),
  )
}

export async function verifyOwnerEmailOtp(
  sessionToken: string,
  payload: VerifyOwnerEmailOtpPayloadDto,
): Promise<void> {
  await publicApi.post(
    OWNER_ONBOARDING_ENDPOINT_PATHS.EMAIL_OTP_VERIFY,
    payload,
    sessionHeaders(sessionToken),
  )
}

export async function getOwnerKycStatus(sessionToken: string): Promise<OwnerKycState> {
  const { data } = await publicApi.get<OwnerApiEnvelope<OwnerKycStatusDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.KYC_STATUS,
    sessionHeaders(sessionToken),
  )
  return parseOwnerKycStatus(data.data)
}

/**
 * Resets a rejected check and issues a fresh token for it.
 *
 * Deliberately its own call rather than a second `startOwnerKycSession`: the server
 * resets the SumSub applicant here, and doing that on the ordinary session path
 * would wipe a verification in progress every time the SDK refreshed its token.
 */
export async function retryOwnerKycSession(sessionToken: string): Promise<OwnerKycSession> {
  const { data } = await publicApi.post<OwnerApiEnvelope<OwnerKycSessionDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.KYC_RETRY,
    {},
    sessionHeaders(sessionToken),
  )
  return parseOwnerKycSession(data.data)
}

// ─── Individual owner ────────────────────────────────────────────────────────

export async function submitOwnerPersonalDetails(
  sessionToken: string,
  input: SubmitPersonalDetailsInput,
): Promise<void> {
  const payload: SubmitPersonalDetailsPayloadDto = {
    dateOfBirth: input.dateOfBirth,
    nationalityCountryId: input.nationalityCountryId,
    residenceCountryId: input.residenceCountryId,
    addressLine1: input.addressLine1.trim(),
    addressLine2: input.addressLine2.trim(),
    city: input.city.trim(),
    stateProvince: input.stateProvince.trim(),
    postalCode: input.postalCode.trim(),
    addressCountryId: input.addressCountryId,
  }
  await publicApi.post(
    OWNER_ONBOARDING_ENDPOINT_PATHS.PERSONAL_DETAILS,
    omitBlank(payload),
    sessionHeaders(sessionToken),
  )
}

// ─── Company owner ───────────────────────────────────────────────────────────

export async function submitOwnerCompanyProfile(
  sessionToken: string,
  input: SubmitCompanyProfileInput,
): Promise<void> {
  const payload: SubmitCompanyProfilePayloadDto = {
    companyName: input.companyName.trim(),
    tradeLicenseNumber: input.tradeLicenseNumber.trim(),
    tradeLicenseExpiry: input.tradeLicenseExpiry,
    countryOfIncorporation: input.countryOfIncorporation,
    registeredAddress: input.registeredAddress.trim(),
    memorandumOfAssociation: input.memorandumOfAssociation.trim(),
    uboDeclarationProvided: input.uboDeclarationProvided,
  }
  await publicApi.post(
    OWNER_ONBOARDING_ENDPOINT_PATHS.COMPANY_PROFILE,
    // `uboDeclarationProvided: false` is a real answer, so it survives the strip —
    // `omitBlank` only drops blanks and nullish values, never `false`.
    omitBlank(payload),
    sessionHeaders(sessionToken),
  )
}

/**
 * PUT, not POST: this replaces the representative set in full, so resending the
 * same body twice leaves the same state.
 */
export async function replaceOwnerRepresentatives(
  sessionToken: string,
  representatives: OwnerRepresentativeInput[],
): Promise<void> {
  const payload: ReplaceRepresentativesPayloadDto = {
    representatives: representatives.map(
      (representative) =>
        omitBlank({
          firstName: representative.firstName.trim(),
          lastName: representative.lastName.trim(),
          email: representative.email.trim(),
          phoneCountryCode: representative.phoneCountryCode.trim(),
          phoneNumber: representative.phoneNumber.trim(),
          dateOfBirth: representative.dateOfBirth,
          nationalityCountryId: representative.nationalityCountryId,
          representativeRole: representative.representativeRole,
          shareholdingPercentage: representative.shareholdingPercentage.trim(),
        }) as RepresentativePayloadDto,
    ),
  }
  await publicApi.put(
    OWNER_ONBOARDING_ENDPOINT_PATHS.REPRESENTATIVES,
    payload,
    sessionHeaders(sessionToken),
  )
}

// ─── Payout account ──────────────────────────────────────────────────────────

export async function getOwnerBankAccount(sessionToken: string): Promise<OwnerBankAccount> {
  const { data } = await publicApi.get<OwnerApiEnvelope<OwnerBankAccountDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.BANK_ACCOUNT,
    sessionHeaders(sessionToken),
  )
  return parseOwnerBankAccount(data.data)
}

export async function submitOwnerBankAccount(
  sessionToken: string,
  input: SubmitBankAccountInput,
): Promise<OwnerBankAccount> {
  const payload: SubmitBankAccountPayloadDto = {
    accountHolderName: input.accountHolderName.trim(),
    bankName: input.bankName.trim(),
    bankCountryId: input.bankCountryId,
    currency: input.currency,
    accountNumber: input.accountNumber.trim(),
    iban: input.iban.trim().toUpperCase(),
    ifscCode: input.ifscCode.trim().toUpperCase(),
    swiftBic: input.swiftBic.trim().toUpperCase(),
    branchName: input.branchName.trim(),
  }
  const { data } = await publicApi.post<OwnerApiEnvelope<OwnerBankAccountDto>>(
    OWNER_ONBOARDING_ENDPOINT_PATHS.BANK_ACCOUNT,
    omitBlank(payload),
    sessionHeaders(sessionToken),
  )
  return parseOwnerBankAccount(data.data)
}

// ─── Closing the flow ────────────────────────────────────────────────────────

export async function confirmOwnerOwnership(
  sessionToken: string,
  input: ConfirmOwnershipInput,
): Promise<void> {
  const payload: ConfirmOwnershipPayloadDto = {
    confirmedOwnershipPercentage: input.confirmedOwnershipPercentage,
    hasAcceptedDeclaration: input.hasAcceptedDeclaration,
  }
  await publicApi.post(
    OWNER_ONBOARDING_ENDPOINT_PATHS.OWNERSHIP_CONFIRMATION,
    payload,
    sessionHeaders(sessionToken),
  )
}

/**
 * Takes no body on purpose: the session already pins which owner and which
 * request are being submitted, so there is nothing the client could send that
 * would not also let it submit someone else's onboarding.
 */
export async function completeOwnerOnboarding(sessionToken: string): Promise<void> {
  await publicApi.post(OWNER_ONBOARDING_ENDPOINT_PATHS.COMPLETE, {}, sessionHeaders(sessionToken))
}
